import {Role} from "@shared/api/models/query/role.model";
import {Organisation} from "@shared/api/models/query/organisation.model";
import {Service} from "@shared/api/models/query/service.model";
import {DepartmentBasic} from "@shared/api/models/query/department.model";
import {License} from "@shared/api/models/query/license.model";
import {OtpModeEnum} from "@shared/api/models/query/otp-mode-history.model";

/**
 * Derived lifecycle status of an admin-dashboard user, as computed by the
 * backend `ReadAllUsersHandler::deriveStatus()` (api.cloud.univa.health,
 * GET /api/v2/users). NOTE: this is a different status set from the patient
 * caseload's `PatientStatusEnum` — do not interchange them.
 */
export enum AdminUserStatusEnum {
    DRAFT = 'draft',
    /**
     * User registered but never logged in — derived by the backend from
     * `firstLoginAt === null` (UNIVAPLAT-2797, GET /api/v2/users). Unlike
     * DRAFT it is visible and filterable in the admin dashboard
     * (UNIVAPLAT-2798) and is included in ADMIN_DASHBOARD_DEFAULT_STATUSES
     * automatically via the Object.values() derivation below.
     */
    NOT_LOGGED_IN = 'not-logged-in',
    FIRST_LOGIN_COMPLETED = 'first-login-completed',
    ONBOARDING_COMPLETED = 'onboarding-completed',
    ACTIVE = 'active',
    COMPLETED = 'completed',
}

/**
 * Default status filter for the admin dashboard — every status except
 * `draft` (UNIVAPLAT-2357). The backend applies derived-status filtering
 * only when a `status` param is present in the request, so the frontend
 * must always send an explicit non-draft status list by default (mirrors
 * DashboardV2Page's `'onboarding,active,completed'` pattern). Derived from
 * AdminUserStatusEnum rather than hardcoded so a future status added to the
 * enum does not silently disappear from the dashboard by default.
 */
export const ADMIN_DASHBOARD_DEFAULT_STATUSES: string[] = Object.values(AdminUserStatusEnum)
    .filter(status => status !== AdminUserStatusEnum.DRAFT);

/**
 * Engagement filter values for the admin dashboard (UNIVAPLAT-2574).
 * Matches the backend `engagement` query param exposed by UNIVAPLAT-2573
 * (GET /api/v2/users). This enum carries only the three *fixed*
 * classifications — `onboarding` was added by the UNIVAPLAT-2573 fix for
 * logged-in users not yet `Active` (still onboarding, no care programme),
 * classified with backend precedence `never_logged_in → onboarding →
 * week_<n>_not_completed → nicely_engaged`. The week-based values follow
 * the mechanical pattern `week_<n>_not_completed` for weeks
 * 1..ADMIN_DASHBOARD_ENGAGEMENT_WEEKS, so they are not enum members — they
 * are built by `buildEngagementWeekValue()` and enumerated as UI options in
 * `ADMIN_DASHBOARD_ENGAGEMENT_OPTIONS` below.
 */
export enum AdminUserEngagementEnum {
    NICELY_ENGAGED = 'nicely_engaged',
    NEVER_LOGGED_IN = 'never_logged_in',
    ONBOARDING = 'onboarding',
}

/** Number of `week_<n>_not_completed` engagement options (weeks 1..20). */
export const ADMIN_DASHBOARD_ENGAGEMENT_WEEKS = 20;

/**
 * Any legal value of the `engagement` query param (UNIVAPLAT-2573/2574):
 * one of the fixed classifications, or a `week_<n>_not_completed`
 * string. The template-literal union makes typos (e.g. 'nicley_engaged')
 * unrepresentable at compile time. `${AdminUserEngagementEnum}` is used
 * instead of the bare enum type so plain string literals ('nicely_engaged')
 * stay assignable alongside enum members — no cast cascade at call sites.
 */
export type AdminUserEngagementValue =
    | `${AdminUserEngagementEnum}`
    | `week_${number}_not_completed`;

/** Builds the backend contract value for a week-based engagement option. */
export function buildEngagementWeekValue(week: number): AdminUserEngagementValue {
    return `week_${week}_not_completed`;
}

/**
 * Full engagement option set for the admin dashboard filter UI
 * (UNIVAPLAT-2574): the fixed classifications first (`onboarding` sits
 * between `never_logged_in` and the week options), then the
 * ADMIN_DASHBOARD_ENGAGEMENT_WEEKS week-based options. Defined here — next
 * to AdminUserEngagementEnum, mirroring ADMIN_DASHBOARD_DEFAULT_STATUSES —
 * so the whole `engagement` API contract lives in one place instead of
 * being split between this model and the filter component. The labels are
 * the exact copy from the ticket description ("Week 1 — not completed",
 * em dash).
 */
export const ADMIN_DASHBOARD_ENGAGEMENT_OPTIONS: { name: string; value: AdminUserEngagementValue }[] = [
    {name: 'Nicely Engaged', value: AdminUserEngagementEnum.NICELY_ENGAGED},
    {name: 'Never Logged In', value: AdminUserEngagementEnum.NEVER_LOGGED_IN},
    {name: 'Onboarding', value: AdminUserEngagementEnum.ONBOARDING},
    ...Array.from({length: ADMIN_DASHBOARD_ENGAGEMENT_WEEKS}, (_, i) => ({
        name: `Week ${i + 1} — not completed`,
        value: buildEngagementWeekValue(i + 1),
    })),
];

/**
 * Reverse lookup from an engagement wire value (e.g. `week_3_not_completed`)
 * to its human-readable label (e.g. "Week 3 — not completed"). Built from
 * ADMIN_DASHBOARD_ENGAGEMENT_OPTIONS so the label copy has a single source
 * of truth — the filter dropdown and the table column can never drift.
 * Falls back to the raw wire value for anything unknown (defensive: e.g. a
 * backend week range extended beyond ADMIN_DASHBOARD_ENGAGEMENT_WEEKS).
 */
const ENGAGEMENT_LABEL_BY_VALUE: ReadonlyMap<AdminUserEngagementValue, string> = new Map(
    ADMIN_DASHBOARD_ENGAGEMENT_OPTIONS.map(option => [option.value, option.name])
);

export function getEngagementLabel(value: AdminUserEngagementValue): string {
    return ENGAGEMENT_LABEL_BY_VALUE.get(value) ?? value;
}

/** Matches (and captures the week number of) a `week_<n>_not_completed` value. */
const ENGAGEMENT_WEEK_VALUE_PATTERN = /^week_(\d+)_not_completed$/;

function engagementWeekNumber(value: AdminUserEngagementValue): number {
    const match = ENGAGEMENT_WEEK_VALUE_PATTERN.exec(value);
    return match ? Number(match[1]) : 0;
}

/**
 * One badge to render in the admin dashboard's Engagement column
 * (UNIVAPLAT-2574 UX follow-up).
 */
export interface EngagementBadgeView {
    /**
     * Wire value driving the badge colour (AdminDashboardPage's
     * engagementBadgeClass()). For the collapsed multi-week badge this is
     * the lowest week value — all week buckets share the warning style, so
     * any representative week yields the correct class.
     */
    value: AdminUserEngagementValue;
    label: string;
    /**
     * Full week breakdown for the collapsed multi-week badge (plain string,
     * one label per incomplete week). `null` for every individual badge —
     * bind to `ngbTooltip`, which stays disabled on falsy content.
     */
    tooltip: string | null;
}

/**
 * Maps a row's engagement bucket array to the badges the Engagement column
 * should render (UNIVAPLAT-2574 UX follow-up, 2026-08-19):
 *
 * - 0 or 1 `week_<n>_not_completed` values → one badge per bucket,
 *   unchanged (label via getEngagementLabel(), no tooltip).
 * - 2+ `week_<n>_not_completed` values → collapsed into a SINGLE badge
 *   labelled "<count> weeks not completed", with the per-week breakdown
 *   ("Week 3 — not completed, Week 5 — not completed", weeks ascending)
 *   carried in `tooltip`.
 *
 * Per backend precedence (`UserEngagementClassifier`), `never_logged_in` /
 * `onboarding` / `nicely_engaged` are always solo, so a multi-bucket array
 * can only ever contain week values — the pass-through of non-week values
 * alongside a collapsed badge below is purely defensive.
 */
export function buildEngagementBadges(engagement: AdminUserEngagementValue[]): EngagementBadgeView[] {
    const weekValues = engagement.filter(value => ENGAGEMENT_WEEK_VALUE_PATTERN.test(value));
    const singleBadge = (value: AdminUserEngagementValue): EngagementBadgeView =>
        ({value, label: getEngagementLabel(value), tooltip: null});

    if (weekValues.length < 2) {
        return engagement.map(singleBadge);
    }

    const sortedWeeks = [...weekValues].sort((a, b) => engagementWeekNumber(a) - engagementWeekNumber(b));
    const collapsed: EngagementBadgeView = {
        value: sortedWeeks[0],
        label: `${sortedWeeks.length} weeks not completed`,
        tooltip: sortedWeeks.map(value => getEngagementLabel(value)).join(', '),
    };
    const nonWeekBadges = engagement
        .filter(value => !ENGAGEMENT_WEEK_VALUE_PATTERN.test(value))
        .map(singleBadge);

    return [collapsed, ...nonWeekBadges];
}

/**
 * Admin user model for admin dashboard
 * Represents user data returned from /api/v2/users?type=cdss&role=patient,carer
 */
export interface AdminUser {
    id: number;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    type: string;
    role: Role;
    category: 'DTC' | 'NHS';
    organisation: Organisation | null;
    service: Service | null;
    department: DepartmentBasic | null;
    carer: AdminUserConnection | null;
    patient: AdminUserConnection | null;
    userCareProgramme: UserCareProgramme | null;
    /**
     * Engagement classification bucket(s) for this user, computed by the
     * backend `UserEngagementClassifier` and returned on every row
     * (AdminDashboardUserListItemResponse::$engagement, GET /api/v2/users).
     * A user can be in SEVERAL week buckets at once (e.g. behind on both
     * week 3 and week 5) — only `never_logged_in` and `onboarding` are
     * ever solo. Resolve labels via getEngagementLabel().
     */
    engagement: AdminUserEngagementValue[];
    firstLoginAt: string | null;
    lastLoginAt: string | null;
    license: License | null;
    createdAt: string;
    /**
     * Current OTP delivery mode for this user (UNIVAPLAT-2990, backend
     * contract UNIVAPLAT-2977). `static` when an admin has pinned a fixed
     * code via `POST /api/v2/admin/users/{userId}/otp-mode/static`,
     * `dynamic` otherwise (the default).
     */
    otpMode: OtpModeEnum;
    /**
     * UNIVAPLAT-2991 (backend contract UNIVAPLAT-2977): mirrors the `isTest`
     * flag already carried on the full `User` entity (see `user.model.ts`).
     * The admin-dashboard row menu hides the OTP settings group entirely
     * for test users — static/dynamic OTP switching is only meaningful for
     * real end users.
     */
    isTest: boolean;
}

// Connected user (carer or patient)
export interface AdminUserConnection {
    id: number;
    firstName: string;
    lastName: string;
}

 // User care programme information
export interface UserCareProgramme {
    durationInWeeks: number;
    currentWeek: number;
    numberOfModules: number;
    modulesCompleted: number;
    careProgramme: string;
}

//  Filters for admin dashboard
export interface AdminDashboardFilters {
    // AdminUserEngagementEnum values and/or `week_<n>_not_completed` strings (UNIVAPLAT-2574)
    engagement?: AdminUserEngagementValue[];
    role?: string[];
    license?: string[];
    careProgramme?: string[];
    signupDateStart?: string;
    signupDateEnd?: string;
    category?: string[];
    organisation?: number[] | string[];
    service?: number[] | string[];
    department?: number[] | string[];
    status?: string[];
    currentWeek?: number[];
    includeDemo?: boolean;
    firstLoginDateStart?: string;
    firstLoginDateEnd?: string;
    lastLoginDateStart?: string;
    lastLoginDateEnd?: string;
}

